import { ProductCode } from "./product-code.constant";
import { ServiceRequest } from "./service-request.constant";
import { DocumentType } from "../interfaces/claim.interface";

/**
 * Product codes whose claim flow collects the "Copy of IC/Passport" upload as part of the
 * Personal Details step (rather than the Supporting Documents step).
 */
export const IDENTITY_DOCUMENT_PRODUCT_CODES: ProductCode[] = [
    ProductCode.EP,
    ProductCode.GP,
    ProductCode.TM_MP,
    ProductCode.AP,
    // [16/7/2026] CH_EP/CH_GP fulfil under TMIM since the Chubb exit — generic flow collects identity doc
    ProductCode.CH_EP,
    ProductCode.CH_GP,
];

export enum ClaimDocumentType {
    COPY_IC_PASSPORT = "identification",
    PHOTOS_DAMAGED_PRODUCT = "damaged_device",
    POLICE_REPORT = "police_report",
    PROOF_PURCHASE = "proof_of_purchase",
    OTHERS = "others",
    FRUIT_PHOTO = "fruit_photo",
    PROOF_DELIVERY = "proof_of_delivery",
    MEDICAL_BILL = "medical_bill",
    DIAGNOSIS_LETTER = "diagnosis_letter",
    PHOTOS_ALLERGIC_REACTION = "allergic_reaction",
    DEATH_CERTIFICATE = "death_certificate",
}

export const DOCUMENT_TYPES: Record<ClaimDocumentType, DocumentType> = {
    [ClaimDocumentType.COPY_IC_PASSPORT]: {
        id: ClaimDocumentType.COPY_IC_PASSPORT,
        name: "Copy of IC/Passport",
        description: "Please upload a clear photo of your IC (front and back) or passport for verification.",
        isRequired: true,
    },
    [ClaimDocumentType.PHOTOS_DAMAGED_PRODUCT]: {
        id: ClaimDocumentType.PHOTOS_DAMAGED_PRODUCT,
        name: "Photos of Damaged Product",
        description: "Please upload clear photos showing the damage to your product.",
        isRequired: true,
    },
    [ClaimDocumentType.POLICE_REPORT]: {
        id: ClaimDocumentType.POLICE_REPORT,
        name: "Police Report",
        description: "Upload a copy of your police report.",
        isRequired: true,
    },
    [ClaimDocumentType.PROOF_PURCHASE]: {
        id: ClaimDocumentType.PROOF_PURCHASE,
        name: "Proof of Purchase",
        description: "Please upload proof of your purchase.",
        isRequired: true,
    },
    [ClaimDocumentType.OTHERS]: {
        id: ClaimDocumentType.OTHERS,
        name: "Other supporting documents",
        description: "Please upload other supporting documents here.",
        isRequired: false,
    },
    [ClaimDocumentType.FRUIT_PHOTO]: {
        id: ClaimDocumentType.FRUIT_PHOTO,
        name: "Upload Fruit Photo",
        description: "Upload clear photos of the fruit for quality verification.",
        isRequired: true,
    },
    [ClaimDocumentType.PROOF_DELIVERY]: {
        id: ClaimDocumentType.PROOF_DELIVERY,
        name: "Proof of Delivery",
        description: "Upload a clear photo or file of the proof of delivery.",
        isRequired: true,
    },
    [ClaimDocumentType.MEDICAL_BILL]: {
        id: ClaimDocumentType.MEDICAL_BILL,
        name: "Itemized Medical Bill",
        description:
            "Please upload a copy of the itemized bill showing the medical expenses incurred for the allergy treatment.",
        isRequired: true,
    },
    [ClaimDocumentType.DIAGNOSIS_LETTER]: {
        id: ClaimDocumentType.DIAGNOSIS_LETTER,
        name: "Doctor's Diagnosis Letter",
        description:
            "Please upload a diagnosis letter or medical report confirming the allergic reaction and treatment provided.",
        isRequired: true,
    },
    [ClaimDocumentType.PHOTOS_ALLERGIC_REACTION]: {
        id: ClaimDocumentType.PHOTOS_ALLERGIC_REACTION,
        name: "Photos of Allergic Reaction",
        description: "Please upload clear photos showing the area affected by the allergic reaction.",
        isRequired: true,
    },
    [ClaimDocumentType.DEATH_CERTIFICATE]: {
        id: ClaimDocumentType.DEATH_CERTIFICATE,
        name: "Death Certificate / Postmortem Report",
        description: "Please upload a copy of the death certificate or postmortem report.",
        isRequired: true,
    },
};

/** Theft claims only require a police report for products worth RM500 or more. */
const POLICE_REPORT_ABOVE_500: DocumentType = {
    ...DOCUMENT_TYPES[ClaimDocumentType.POLICE_REPORT],
    minProductPrice: 500,
};

export const SERVICE_DOCUMENTS_MAPPING: Record<ProductCode, Partial<Record<ServiceRequest, DocumentType[]>>> = {
    [ProductCode.EP]: {
        [ServiceRequest.ACCIDENTAL_DAMAGE_LIQUID_DAMAGE]: [
            DOCUMENT_TYPES[ClaimDocumentType.PHOTOS_DAMAGED_PRODUCT],
            DOCUMENT_TYPES[ClaimDocumentType.PROOF_PURCHASE],
        ],
        [ServiceRequest.CHUBB_THEFT]: [
            POLICE_REPORT_ABOVE_500,
            DOCUMENT_TYPES[ClaimDocumentType.PROOF_PURCHASE],
        ],
        [ServiceRequest.DAMAGE_TRANSIT]: [
            DOCUMENT_TYPES[ClaimDocumentType.PHOTOS_DAMAGED_PRODUCT],
            DOCUMENT_TYPES[ClaimDocumentType.PROOF_PURCHASE],
        ],
        [ServiceRequest.MANUFACTURER_DEFECTS]: [
            DOCUMENT_TYPES[ClaimDocumentType.PHOTOS_DAMAGED_PRODUCT],
            DOCUMENT_TYPES[ClaimDocumentType.PROOF_PURCHASE],
        ],
    },
    [ProductCode.GP]: {
        [ServiceRequest.ACCIDENTAL_DAMAGE_LIQUID_DAMAGE]: [
            DOCUMENT_TYPES[ClaimDocumentType.PHOTOS_DAMAGED_PRODUCT],
            DOCUMENT_TYPES[ClaimDocumentType.PROOF_PURCHASE],
        ],
        [ServiceRequest.CHUBB_THEFT]: [
            POLICE_REPORT_ABOVE_500,
            DOCUMENT_TYPES[ClaimDocumentType.PROOF_PURCHASE],
        ],
        [ServiceRequest.DAMAGE_TRANSIT]: [
            DOCUMENT_TYPES[ClaimDocumentType.PHOTOS_DAMAGED_PRODUCT],
            DOCUMENT_TYPES[ClaimDocumentType.PROOF_PURCHASE],
        ],
        [ServiceRequest.MANUFACTURER_DEFECTS]: [
            DOCUMENT_TYPES[ClaimDocumentType.PHOTOS_DAMAGED_PRODUCT],
            DOCUMENT_TYPES[ClaimDocumentType.PROOF_PURCHASE],
        ],
    },
    [ProductCode.TM_MP]: {
        [ServiceRequest.ACCIDENTAL_DAMAGE_LIQUID_DAMAGE]: [
            DOCUMENT_TYPES[ClaimDocumentType.PHOTOS_DAMAGED_PRODUCT],
            DOCUMENT_TYPES[ClaimDocumentType.PROOF_PURCHASE],
        ],
        [ServiceRequest.CHUBB_THEFT]: [
            POLICE_REPORT_ABOVE_500,
            DOCUMENT_TYPES[ClaimDocumentType.PROOF_PURCHASE],
        ],
        [ServiceRequest.DAMAGE_TRANSIT]: [
            DOCUMENT_TYPES[ClaimDocumentType.PHOTOS_DAMAGED_PRODUCT],
            DOCUMENT_TYPES[ClaimDocumentType.PROOF_PURCHASE],
        ],
        [ServiceRequest.MANUFACTURER_DEFECTS]: [
            DOCUMENT_TYPES[ClaimDocumentType.PHOTOS_DAMAGED_PRODUCT],
            DOCUMENT_TYPES[ClaimDocumentType.PROOF_PURCHASE],
        ],
    },
    [ProductCode.AP]: {
        [ServiceRequest.MEDICAL_EXPENSES]: [
            DOCUMENT_TYPES[ClaimDocumentType.MEDICAL_BILL],
            DOCUMENT_TYPES[ClaimDocumentType.DIAGNOSIS_LETTER],
            DOCUMENT_TYPES[ClaimDocumentType.PHOTOS_ALLERGIC_REACTION],
        ],
        [ServiceRequest.ITEM_PURCHASED_VALUE_REIMBURSEMENT]: [
            DOCUMENT_TYPES[ClaimDocumentType.PHOTOS_ALLERGIC_REACTION],
        ],
        [ServiceRequest.EMERGENCY_HOSPITALIZATION]: [
            DOCUMENT_TYPES[ClaimDocumentType.MEDICAL_BILL],
            DOCUMENT_TYPES[ClaimDocumentType.DIAGNOSIS_LETTER],
            DOCUMENT_TYPES[ClaimDocumentType.PHOTOS_ALLERGIC_REACTION],
        ],
        [ServiceRequest.ACCIDENTAL_DEATH]: [DOCUMENT_TYPES[ClaimDocumentType.DEATH_CERTIFICATE]],
    },
    // [16/7/2026] CH_EP/CH_GP fulfil under TMIM since the Chubb exit — generic claim types only.
    // Legacy Chubb-stamped policies use CHUBB_SERVICE_DOCUMENTS_MAPPING below instead.
    [ProductCode.CH_GP]: {
        [ServiceRequest.ACCIDENTAL_DAMAGE_LIQUID_DAMAGE]: [
            DOCUMENT_TYPES[ClaimDocumentType.PHOTOS_DAMAGED_PRODUCT],
            DOCUMENT_TYPES[ClaimDocumentType.PROOF_PURCHASE],
        ],
        [ServiceRequest.CHUBB_THEFT]: [
            POLICE_REPORT_ABOVE_500,
            DOCUMENT_TYPES[ClaimDocumentType.PROOF_PURCHASE],
        ],
        [ServiceRequest.DAMAGE_TRANSIT]: [
            DOCUMENT_TYPES[ClaimDocumentType.PHOTOS_DAMAGED_PRODUCT],
            DOCUMENT_TYPES[ClaimDocumentType.PROOF_PURCHASE],
        ],
        [ServiceRequest.MANUFACTURER_DEFECTS]: [
            DOCUMENT_TYPES[ClaimDocumentType.PHOTOS_DAMAGED_PRODUCT],
            DOCUMENT_TYPES[ClaimDocumentType.PROOF_PURCHASE],
        ],
    },
    [ProductCode.CH_EP]: {
        [ServiceRequest.ACCIDENTAL_DAMAGE_LIQUID_DAMAGE]: [
            DOCUMENT_TYPES[ClaimDocumentType.PHOTOS_DAMAGED_PRODUCT],
            DOCUMENT_TYPES[ClaimDocumentType.PROOF_PURCHASE],
        ],
        [ServiceRequest.CHUBB_THEFT]: [
            POLICE_REPORT_ABOVE_500,
            DOCUMENT_TYPES[ClaimDocumentType.PROOF_PURCHASE],
        ],
        [ServiceRequest.DAMAGE_TRANSIT]: [
            DOCUMENT_TYPES[ClaimDocumentType.PHOTOS_DAMAGED_PRODUCT],
            DOCUMENT_TYPES[ClaimDocumentType.PROOF_PURCHASE],
        ],
        [ServiceRequest.MANUFACTURER_DEFECTS]: [
            DOCUMENT_TYPES[ClaimDocumentType.PHOTOS_DAMAGED_PRODUCT],
            DOCUMENT_TYPES[ClaimDocumentType.PROOF_PURCHASE],
        ],
    },
    // [16/7/2026] CH_MP retired (zero policies)
    //[ProductCode.CH_MP]: {
    //    [ServiceRequest.CHUBB_ACCIDENTAL_DAMAGE]: [
    //        DOCUMENT_TYPES[ClaimDocumentType.PHOTOS_DAMAGED_PRODUCT],
    //        DOCUMENT_TYPES[ClaimDocumentType.OTHERS],
    //    ],
    //    [ServiceRequest.CHUBB_THEFT]: [
    //        DOCUMENT_TYPES[ClaimDocumentType.POLICE_REPORT],
    //        DOCUMENT_TYPES[ClaimDocumentType.OTHERS],
    //    ],
    //},
    [ProductCode.AIA_FG]: {},
};

/**
 * Chubb portal vocabulary, used only by ChubbClaimsComponent for legacy Chubb-stamped policies.
 * Mirrors ChubbProductClaimDocumentMap in the tts-webapi backend.
 * Removable once the last Chubb-stamped policy is past its claim window (~Aug 2027).
 */
export const CHUBB_SERVICE_DOCUMENTS_MAPPING: Partial<
    Record<ProductCode, Partial<Record<ServiceRequest, DocumentType[]>>>
> = {
    [ProductCode.CH_GP]: {
        [ServiceRequest.CHUBB_ACCIDENTAL_DAMAGE]: [
            DOCUMENT_TYPES[ClaimDocumentType.PHOTOS_DAMAGED_PRODUCT],
            DOCUMENT_TYPES[ClaimDocumentType.OTHERS],
        ],
        [ServiceRequest.CHUBB_THEFT]: [
            DOCUMENT_TYPES[ClaimDocumentType.POLICE_REPORT],
            DOCUMENT_TYPES[ClaimDocumentType.OTHERS],
        ],
    },
    [ProductCode.CH_EP]: {
        [ServiceRequest.CHUBB_ACCIDENTAL_DAMAGE]: [
            DOCUMENT_TYPES[ClaimDocumentType.PHOTOS_DAMAGED_PRODUCT],
            DOCUMENT_TYPES[ClaimDocumentType.OTHERS],
        ],
        [ServiceRequest.CHUBB_THEFT]: [
            DOCUMENT_TYPES[ClaimDocumentType.POLICE_REPORT],
            DOCUMENT_TYPES[ClaimDocumentType.OTHERS],
        ],
    },
    // [16/7/2026] CH_MP retired (zero policies)
    //[ProductCode.CH_MP]: {
    //    [ServiceRequest.CHUBB_ACCIDENTAL_DAMAGE]: [
    //        DOCUMENT_TYPES[ClaimDocumentType.PHOTOS_DAMAGED_PRODUCT],
    //        DOCUMENT_TYPES[ClaimDocumentType.OTHERS],
    //    ],
    //    [ServiceRequest.CHUBB_THEFT]: [
    //        DOCUMENT_TYPES[ClaimDocumentType.POLICE_REPORT],
    //        DOCUMENT_TYPES[ClaimDocumentType.OTHERS],
    //    ],
    //},
};
